import { PropsWithChildren, useEffect, useState } from 'react'
import { useGetSetting } from 'api/settings'
import chroma from 'chroma-js'
import { useThemePreference } from 'hooks/useThemePreference'
import { ThemeEnum } from 'types/setting'

export function generateTailwindColorPalette(baseColor: string) {
  const color = chroma(baseColor)

  return {
    50: color.brighten(2.5).hex(),
    100: color.brighten(2).hex(),
    200: color.brighten(1.5).hex(),
    300: color.brighten(1).hex(),
    400: color.brighten(0.5).hex(),
    500: color.hex(),
    600: color.darken(0.5).hex(),
    700: color.darken(1).hex(),
    800: color.darken(1.5).hex(),
    900: color.darken(1.5).hex(),
  }
}

export const generateColorPalette = (baseColor: string) => {
  const color = chroma(baseColor)
  const hue = color.get('hsl.h')
  const saturation = color.get('hsl.s')

  return {
    50: chroma.hsl(hue, saturation * 0.92, 0.96).hex(),
    100: chroma.hsl(hue, saturation * 0.95, 0.9).hex(),
    200: chroma.hsl(hue, saturation * 0.98, 0.85).hex(),
    300: chroma.hsl(hue, saturation, 0.78).hex(),
    400: chroma.hsl(hue, saturation, 0.68).hex(),
    500: color.hex(),
    600: chroma.hsl(hue, saturation * 1.05, 0.45).hex(),
    700: chroma.hsl(hue, saturation * 1.1, 0.35).hex(),
    800: chroma.hsl(hue, saturation * 1.15, 0.25).hex(),
    900: chroma.hsl(hue, saturation * 1.2, 0.17).hex(),
  }
}

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const { setting } = useGetSetting()
  const themePreference = useThemePreference()
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const initializeTheme = () => {
      if (setting) {
        console.debug('initializeTheme', { setting })
        // const root = document.documentElement
        // root.style.setProperty('color-primary', setting.primaryColor)
        // root.style.setProperty('color-secondary', setting.secondaryColor)

        if (setting.primaryColor && setting.secondaryColor) {
          const primaryPalette = generateTailwindColorPalette(setting.primaryColor)
          const secondaryPalette = generateTailwindColorPalette(setting.secondaryColor)

          // Set CSS variables
          const root = document.documentElement

          Object.entries(primaryPalette).forEach(([shade, color]) => {
            root.style.setProperty(`--primary-${shade}`, color)
          })

          // Gradient: $primary → $primary with (H + 20) (L - 8)
          const primaryChroma = chroma(setting.primaryColor)
          const h = primaryChroma.get('hsl.h')
          const s = primaryChroma.get('hsl.s')
          const l = primaryChroma.get('hsl.l')
          const gradientEnd = chroma.hsl(h + 20, s, Math.max(0, l - 0.08)).hex()
          root.style.setProperty('--primary-gradient-start', setting.primaryColor)
          root.style.setProperty('--primary-gradient-end', gradientEnd)

          Object.entries(secondaryPalette).forEach(([shade, color]) => {
            root.style.setProperty(`--secondary-${shade}`, color)
          })

          // auto: default light, the sidebar ThemeToggle switches at runtime. light/dark: forced.
          root.classList.toggle('dark', themePreference === ThemeEnum.Dark)

          // Set header gradient based on theme (light: linear gradient, dark: radial purple glow)
          const pc = chroma(setting.primaryColor)
          // if (theme === 'dark') {
          //   const headerGradient = `radial-gradient(circle at 50% 30%, ${pc.alpha(0.45).css()} 0%, rgba(0, 0, 0, 0.05) 70%)`
          //   root.style.setProperty('--header-gradient', headerGradient)
          // } else {
          const headerGradient = `linear-gradient(113deg, ${pc.alpha(0.4).css()}, ${pc.alpha(0.8).css()}, ${pc.alpha(0.4).css()})`
          root.style.setProperty('--header-gradient', headerGradient)
          // }

          setIsInitialized(true)
        }

        // const favicon = setting.favicon?.full

        // if (favicon) {
        //   const headTitle = document.querySelector('head')
        //   const setFavicon = document.createElement('link')
        //   setFavicon.setAttribute('rel', 'shortcut icon')
        //   setFavicon.setAttribute('href', favicon)
        //   headTitle?.appendChild(setFavicon)
        // }
      }
    }

    initializeTheme()
  }, [setting, themePreference])

  return isInitialized ? <>{children}</> : null
}
